import { FC } from 'react'
import cn from 'classnames'
import { useGetPlansQuery } from 'common/services/usePlan'
import { Plan } from 'common/types/responses'
import getSymbolFromCurrency from 'currency-symbol-map' // we need this to make JSX compile
import sortBy from 'lodash/sortBy'
import Tooltip from './Tooltip'
type ProductSelectType = {
  value: string
  onChange: (product: string) => void
}

type ProductItemType = {
  product: Plan
  isActive: boolean
  onClick: (price_id: string) => void
  infoText?: React.ReactNode
}

export const ProductItem: FC<ProductItemType> = ({
  infoText,
  isActive,
  onClick,
  product,
  ...rest
}) => {
  const name =
    product.recurring.interval === 'year'
      ? 'Annual'
      : product.recurring.interval_count === 1
      ? 'Monthly'
      : 'Quarterly'

  const periodLabel =
    name === 'Annual' ? 'year' : name === 'Quarterly' ? 'quarter' : 'month'

  const billingLabel =
    name === 'Annual' ? 'year' : name === 'Quarterly' ? 'quarter' : 'month'

  const discountedAmount = product.discounted_amount
  const firstAmount = discountedAmount || product.unit_amount
  const isOffer = product.percent_off || product.amount_off
  let period = 'year'

  if (name === 'Quarterly') {
    period = 'quarter'
  } else if (name === 'Monthly') {
    period = 'month'
  }
  return (
    <div
      onClick={() => onClick(product.id)}
      className={cn(
        'bg-blackLighter px-4-half pt-3-half pb-2-half rounded-lg',
        {
          'bg-turquoise text-blackLight': isActive,
        },
      )}
      {...rest}
    >
      {infoText ? (
        infoText
      ) : isOffer ? (
        <span
          className={cn(
            'text-small d-flex align-items-center justify-content-end',
            {
              'text-blackLight': isActive,
              'text-turquoise': !isActive,
            },
          )}
        >
          Get{' '}
          {product.percent_off
            ? `${product.percent_off}%`
            : `$${product.amount_off / 100}`}{' '}
          off your first {period}
          <svg
            className='ms-1'
            width='22'
            height='23'
            viewBox='0 0 22 23'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M11.8594 7.53906C12.2891 7.625 12.6615 7.69661 12.9766 7.75391C13.4635 7.95443 13.6641 8.3125 13.5781 8.82812C13.4062 9.3151 13.0625 9.5013 12.5469 9.38672C12.3464 9.35807 12.0885 9.3151 11.7734 9.25781C11.6589 9.22917 11.5586 9.21484 11.4727 9.21484C10.8711 9.12891 10.3984 9.17188 10.0547 9.34375C9.76823 9.51562 9.625 9.67318 9.625 9.81641C9.59635 9.95964 9.59635 10.0599 9.625 10.1172C9.625 10.1745 9.69661 10.2461 9.83984 10.332C10.1549 10.5039 10.6419 10.6901 11.3008 10.8906H11.3438C11.974 11.0339 12.6042 11.306 13.2344 11.707C13.5495 11.9076 13.8073 12.2227 14.0078 12.6523C14.1797 13.0534 14.2227 13.4974 14.1367 13.9844C13.9362 14.8724 13.4492 15.4596 12.6758 15.7461C12.4466 15.8607 12.1745 15.9466 11.8594 16.0039V16.3477C11.8021 16.8633 11.5299 17.1497 11.043 17.207C10.5273 17.1497 10.2409 16.8633 10.1836 16.3477V15.9609C9.66797 15.8177 9.16667 15.6602 8.67969 15.4883C8.59375 15.4596 8.52214 15.4453 8.46484 15.4453C7.97786 15.2161 7.80599 14.8438 7.94922 14.3281C8.14974 13.8698 8.50781 13.6979 9.02344 13.8125C9.08073 13.8125 9.13802 13.8268 9.19531 13.8555C9.2526 13.8841 9.29557 13.8984 9.32422 13.8984C9.32422 13.8984 9.33854 13.8984 9.36719 13.8984C9.85417 14.099 10.2552 14.2279 10.5703 14.2852C11.2005 14.3711 11.6875 14.3424 12.0312 14.1992C12.2891 14.056 12.4323 13.8841 12.4609 13.6836C12.4896 13.4831 12.4753 13.3685 12.418 13.3398C12.418 13.2539 12.3607 13.168 12.2461 13.082C11.931 12.8815 11.444 12.6953 10.7852 12.5234L10.6562 12.4805C10.6276 12.4805 10.599 12.4805 10.5703 12.4805C9.9974 12.3086 9.4388 12.0651 8.89453 11.75C8.55078 11.5495 8.27865 11.2487 8.07812 10.8477C7.90625 10.4466 7.86328 9.98828 7.94922 9.47266C8.14974 8.67057 8.6224 8.11198 9.36719 7.79688C9.625 7.68229 9.89714 7.59635 10.1836 7.53906V7.15234C10.2409 6.63672 10.5273 6.35026 11.043 6.29297C11.5299 6.35026 11.8021 6.63672 11.8594 7.15234V7.53906ZM14.7812 2.59766C16.2708 2.42578 17.6029 2.88411 18.7773 3.97266C19.8659 5.11849 20.3242 6.45052 20.1523 7.96875C21.2982 8.88542 21.9141 10.1458 22 11.75C21.9141 13.3542 21.2982 14.6146 20.1523 15.5312C20.3242 17.0208 19.8659 18.3529 18.7773 19.5273C17.6029 20.6159 16.2708 21.0742 14.7812 20.9023C13.8646 22.0482 12.6042 22.6641 11 22.75C9.39583 22.6641 8.13542 22.0482 7.21875 20.9023C5.70052 21.0742 4.36849 20.6159 3.22266 19.5273C2.13411 18.3529 1.67578 17.0208 1.84766 15.5312C0.673177 14.6146 0.0572917 13.3542 0 11.75C0.0572917 10.1458 0.673177 8.88542 1.84766 7.96875C1.67578 6.45052 2.13411 5.11849 3.22266 3.97266C4.36849 2.88411 5.70052 2.42578 7.21875 2.59766C8.13542 1.42318 9.39583 0.807292 11 0.75C12.6042 0.807292 13.8646 1.42318 14.7812 2.59766ZM8.16406 5.00391L7.39062 4.74609C6.35938 4.45964 5.45703 4.6888 4.68359 5.43359C3.9388 6.20703 3.70964 7.10938 3.99609 8.14062L4.25391 8.91406L3.52344 9.34375C2.60677 9.85938 2.11979 10.6615 2.0625 11.75C2.11979 12.8385 2.60677 13.6406 3.52344 14.1562L4.25391 14.543L3.99609 15.3164C3.70964 16.3477 3.9388 17.2643 4.68359 18.0664C5.45703 18.8112 6.35938 19.0404 7.39062 18.7539L8.16406 18.4961L8.59375 19.2695C9.10938 20.1576 9.91146 20.6302 11 20.6875C12.0885 20.6302 12.8906 20.1576 13.4062 19.2695L13.793 18.4961L14.5664 18.7539C15.5977 19.0404 16.5143 18.8112 17.3164 18.0664C18.0612 17.2643 18.2904 16.3477 18.0039 15.3164L17.7461 14.543L18.5195 14.1562C19.4076 13.6406 19.8802 12.8385 19.9375 11.75C19.8802 10.6615 19.4076 9.85938 18.5195 9.34375L17.7461 8.91406L18.0039 8.14062C18.2904 7.10938 18.0612 6.20703 17.3164 5.43359C16.5143 4.6888 15.5977 4.45964 14.5664 4.74609L13.793 5.00391L13.4062 4.27344C12.8906 3.35677 12.0885 2.86979 11 2.8125C9.91146 2.86979 9.10938 3.35677 8.59375 4.27344L8.16406 5.00391Z'
              fill={isActive ? '#282A2C' : '#89E6E3'}
            />
          </svg>
        </span>
      ) : product.featured ? (
        <span
          className={cn('text-small d-flex justify-content-end', {
            'text-blackLight': isActive,
            'text-turquoise': !isActive,
          })}
        >
          Most Popular
          <span
            className={cn('fa fa-sharp fa-regular fa-fire ms-2', {
              'text-blackLight': isActive,
              'text-turquoise': !isActive,
            })}
          />
        </span>
      ) : null}
      <div
        className={cn('d-flex flex-row justify-content-between mt-4', {
          'mt-7': !product.featured,
        })}
      >
        <div className='d-flex flex-column'>
          <div>
            <span className='text-large text-bold'>
              {' '}
              {getSymbolFromCurrency(product.currency.toLowerCase())}
              {name === 'Monthly'
                ? firstAmount / 100
                : name === 'Quarterly'
                ? (firstAmount / 100 / 3).toFixed(2)
                : (firstAmount / 100 / 12).toFixed(2)}
              <span className='text-regular'>
                {!!product.percent_off && ` ${product.percent_off}% off sale`}
              </span>
            </span>
          </div>
          <span className='text-small'>
            <span className='text-semibold'>USD</span>
            {!product.percent_off && <> per {billingLabel}</>}
          </span>
        </div>
        <div className='d-flex flex-column'>
          <span className='text-large text-bold text-uppercase'>
            {name} Member
          </span>
          <span className='text-small d-flex flex-row justify-content-end'>
            {isOffer && (
              <>
                <div
                  className={'text-decoration-line-through me-1-line-through'}
                >
                  {getSymbolFromCurrency(product.currency.toLowerCase())}
                  {product.unit_amount / 100}
                </div>
              </>
            )}
            <div className='ms-1'>
              {getSymbolFromCurrency(product.currency.toLowerCase())}
              {(product.discounted_amount || product.unit_amount) / 100} for
              your first {periodLabel}
            </div>
            {!!isOffer && (
              <div className='position-relative'>
                <Tooltip
                  content={`You will be billed the standard ${getSymbolFromCurrency(
                    product.currency.toLowerCase(),
                  )}${product.unit_amount / 100} after the first ${period}`}
                >
                  <span className='fa fal fa-question-circle ms-2' />{' '}
                </Tooltip>
              </div>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

const ProductSelect: FC<ProductSelectType> = ({ onChange, value }) => {
  const { data } = useGetPlansQuery({})
  return (
    <div className='d-flex flex-column gap-3-half'>
      {sortBy(data, (p) => p.unit_amount).map((v, index) => (
        <ProductItem
          aria-label={`product-${index}`}
          key={v.id}
          product={v}
          onClick={onChange}
          isActive={value === v.id}
        />
      ))}
    </div>
  )
}

export default ProductSelect
